import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Text } from '@rsos/base-components';
import { DebugPanel } from '@rsos/debug-panel';
import {
  combineAudioChunks,
  downloadAudioFile,
} from '@rsos/debug-panel/src/utils/audioFileUtils';
import type { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import { StyledConnectionIndicator } from './AudioDebugPanel.styles';
import { AudioStreamer } from './audioStreamer';
import { useDebugPanelConfig } from './config';
import { SpeakerRole } from './types';
import { useAudioStreamerState } from './useAudioStreamerState';

/* eslint-disable no-console -- because this is a debug panel, fine to use console */

interface AudioDebugPanelProps {
  onError?: (error: string) => void;
  onClose?: () => void;
}

interface AudioDebugState {
  // ui state:
  speakerRole: SpeakerRole;
  error?: string;
}

const AudioDebugPanel: React.FC<AudioDebugPanelProps> = ({
  onError,
  onClose,
}) => {
  const config = useDebugPanelConfig();
  const callerNumber = useCallerNumber();

  const [state, setState] = useState<AudioDebugState>({
    speakerRole: 'caller',
  });

  const streamer = useMemo(() => {
    return new AudioStreamer(config);
  }, [config]);

  const streamerState = useAudioStreamerState(streamer);
  const hasAnyAudioChunks = () => streamerState.recordedAudioChunkCount > 0;

  useEffect(() => {
    streamer.setMessageHandler(message => {
      console.debug('RTT Ingress message:', message);
    });

    streamer.setErrorHandler(error => {
      setState(prev => ({ ...prev, error }));
      onError?.(error);
    });

    return () => {
      // stop recording and clean up on unmount
      console.debug(
        'AudioDebugPanelContent cleanup - stopping previous streamer',
      );
      if (streamer.isRecording()) {
        streamer.stopRecording().catch(error => {
          console.error('Error stopping recording during cleanup:', error);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't want a new streamer to be created so we're explicitly ignoring the deps
  }, []);

  const handleStartRecording = async () => {
    try {
      setState(prev => ({
        ...prev,
        error: undefined,
      }));

      await streamer.startRecording(callerNumber, state.speakerRole);
    } catch (error) {
      console.error('Error starting recording:', error);
      setState(prev => ({
        ...prev,
        error: `Failed to start recording: ${error}`,
      }));
    }
  };

  const handleStopRecording = async () => {
    try {
      await streamer.stopRecording();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: `Failed to stop recording: ${error}`,
      }));
    }
  };

  const handleSpeakerRoleToggle = () => {
    const newSpeakerRole =
      state.speakerRole === 'caller' ? 'dispatcher' : 'caller';

    setState(prev => ({
      ...prev,
      speakerRole: newSpeakerRole,
    }));

    // If currently recording, update the speaker role in the active streamer
    if (streamerState.isRecording && streamer) {
      streamer.setSpeakerRole(newSpeakerRole);
    }
  };

  const handleDownloadMultichannelAudio = () => {
    if (!streamer) {
      setState(prev => ({ ...prev, error: 'No active streamer' }));
      return;
    }

    const recordedChunks = streamer.getRecordedAudioChunks();
    if (recordedChunks.length === 0) {
      setState(prev => ({ ...prev, error: 'No audio recorded to download' }));
      return;
    }

    const blob = combineAudioChunks(recordedChunks);
    const sessionTimestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `${sessionTimestamp}_multichannel_audio.webm`;
    downloadAudioFile(blob, filename);
  };

  return (
    <DebugPanel title="Simulate Realtime Audio" onClose={onClose}>
      <Text size="small" color="primaryText">
        Select a call from the queue. Click "Start Sending Audio" to stream your
        microphone audio to RTT Ingress. Toggle between Citizen and Dispatcher
        roles to simulate a conversation.
      </Text>

      {callerNumber && (
        <Box horizontal align="center">
          <Text color="primaryText">
            <strong>Calling from: {callerNumber}</strong>
          </Text>
          <StyledConnectionIndicator connected={streamerState.isConnected} />
          <Text size="small" color="secondaryText">
            {streamerState.isConnected ? 'Connected' : 'Disconnected'}
          </Text>
        </Box>
      )}

      <Text size="normal" color="primaryText">
        <label>
          <input
            type="checkbox"
            checked={streamerState.isCollectRecordings}
            onChange={e => {
              if (!streamer) {
                throw new Error(
                  'No active streamer in handleCollectRecordingToggle',
                );
              }
              streamer.setCollectRecordings(e.target.checked);
            }}
            disabled={streamerState.isRecording}
          />
          Collect Recording Files
        </label>
      </Text>

      {streamerState.isCollectRecordings && (
        <Text size="small" color="secondaryText">
          Audio will be saved separately for each speaker role during recording.
        </Text>
      )}
      {streamerState.isCollectRecordings && hasAnyAudioChunks() && (
        <Button
          onClick={handleDownloadMultichannelAudio}
          disabled={streamerState.recordedAudioChunkCount === 0}
          noMargin
        >
          🎵 Download Multi-channel Audio (
          {streamerState.recordedAudioChunkCount} chunks)
        </Button>
      )}

      {streamerState.isRecording && (
        <Button
          onClick={handleSpeakerRoleToggle}
          btnType="secondary"
          noMargin
          title="Click to change who is speaking"
        >
          {state.speakerRole === 'caller'
            ? '📞 Citizen Speaking'
            : '🎧 Dispatcher Speaking'}
        </Button>
      )}

      {!streamerState.isRecording ? (
        <Button
          onClick={handleStartRecording}
          disabled={!callerNumber}
          title={callerNumber ? '' : 'Select a call from the queue, first'}
          noMargin
          btnType="success"
        >
          <strong>Start Sending Audio</strong>
        </Button>
      ) : (
        <Button
          onClick={handleStopRecording}
          noMargin
          btnType="warning_primary"
        >
          <strong>End call</strong>
        </Button>
      )}

      {state.error && (
        <Text size="small" color="heavy_danger">
          {state.error}
        </Text>
      )}
    </DebugPanel>
  );
};

// Does its best to return the phone number of the selected event in the event
// queue
function useCallerNumber(): string {
  const selectedCall = useSelectedCall();
  const selectedCallerID = useSelector(
    (state: StateWithEmergencyCalls) =>
      state.emergencyCalls.selectedCallerID || '',
  );

  if (!selectedCall && !selectedCallerID) return '';

  // First try to get from selectedCallerID
  if (selectedCallerID) return selectedCallerID;

  // Try to get from call locations
  if (selectedCall?.core?.locations?.[0]?.caller_id) {
    return selectedCall.core.locations[0].caller_id;
  }

  return '';
}

export default AudioDebugPanel;
